export interface EditorLesson {
  id: number;
  title: string;
  lesson_id: number | null;
  // The slot has held a lesson before, so it keeps its position (R35).
  ever_published?: boolean;
  // Emptied by the trash. Shown empty, but left out of the save: the server
  // puts it back as an empty slot only once the removal dialog has confirmed
  // the lesson's removal, as it did when the trash deleted the slot.
  trashed?: boolean;
}

export type TrashAction = "delete" | "empty" | "kept";

/**
 * What the trash does to a lesson slot. Positions are fixed: a slot that ever
 * held a published lesson stays behind, empty, because students walk past
 * its position. A slot that never did is deleted outright. An emptied slot
 * that held a lesson has nothing left to trash.
 */
export const trashAction = (lesson: EditorLesson): TrashAction => {
  if (!lesson.ever_published) return "delete";
  return lesson.lesson_id === null ? "kept" : "empty";
};

export const trashLessonSlot = (
  lessons: EditorLesson[],
  id: number,
): EditorLesson[] =>
  lessons.flatMap((lesson) => {
    if (lesson.id !== id) return [lesson];
    const action = trashAction(lesson);
    if (action === "delete") return [];
    if (action === "kept") return [lesson];
    return [{ ...lesson, lesson_id: null, trashed: true }];
  });

/** The slots a structure save sends: everything but a trashed slot. */
export const payloadLessons = (lessons: EditorLesson[]) =>
  lessons
    .filter((lesson) => !lesson.trashed)
    .map((lesson, lessonIndex) => ({
      id: lesson.id,
      title: lesson.title,
      lesson_id: lesson.lesson_id,
      status: lesson.lesson_id ? "published" : "placeholder",
      sort_order: lessonIndex,
    }));
